/**
 * XP — tonårsvyns sätt att uttrycka stjärnor.
 *
 * Stjärnorna är fortfarande SANNINGEN: backend räknar, drar och betalar tillbaka i
 * stjärnor (StarBalance, ShopItem.priceStars, Purchase.priceStars). XP är ren
 * PRESENTATION för persona 'teen' — ingen data, inget pris och inget API-anrop
 * uttrycks någonsin i XP.
 *
 * Konstanten låg tidigare som en lokal `xpPerStar` i TeenProgress medan TeenHome och
 * onboardingen multiplicerade med 10 rakt i JSX. Nu när även butiken och köpvyn ska
 * visa XP skulle omräkningen ha funnits i ännu fler versioner — och den dagen någon
 * ändrar växelkursen är det just de bortglömda ställena som börjar ljuga för
 * tonåringen. Därför ett enda ställe.
 */

/** Etablerad växelkurs sedan TeenProgress iteration 2: 10 XP = 1 stjärna. */
export const XP_PER_STAR = 10;

/** Stjärnor → XP. Enda tillåtna omräkningen; multiplicera aldrig med 10 på egen hand. */
export const starsToXp = (stars: number): number => stars * XP_PER_STAR;

/**
 * XP per nivå. Fem stjärnor = en nivå, alltså 50 XP.
 *
 * Låg tidigare som en lokal `xpPerLevel` i TeenProgress medan TeenHome räknade
 * `Math.floor(totalStars / 5) + 1` — samma tal uttryckt på två sätt, i två filer. När
 * headern på dagsidan nu också ska visa nivån hade det blivit tre. Samma skäl som till
 * att växelkursen ovan bor på ett ställe: den dagen någon ändrar trappan är det de
 * bortglömda ställena som börjar ljuga.
 */
export const XP_PER_LEVEL = 50;

/** Nivån utifrån intjänade stjärnor. Nivå 1 är starten, inte noll. */
export const levelFromStars = (stars: number): number =>
  Math.floor(starsToXp(stars) / XP_PER_LEVEL) + 1;

/** XP intjänade INOM den pågående nivån (0 … XP_PER_LEVEL - 1). */
export const xpIntoLevel = (stars: number): number => starsToXp(stars) % XP_PER_LEVEL;

/** Hur långt in i nivån man är, i procent. För progressbaren. */
export const levelProgressPercent = (stars: number): number =>
  (xpIntoLevel(stars) / XP_PER_LEVEL) * 100;

/**
 * i18n-nyckeln för nivåns titel. Titlarna själva är en egen fråga (backlog punkt 10,
 * vilande) — det här är bara trappan, flyttad ut ur TeenProgress så att headern på
 * dagsidan kan visa samma titel som framstegssidan.
 */
export const levelTitleKey = (level: number): string => {
  if (level >= 20) return 'teen.progress.levelTitleLegend';
  if (level >= 15) return 'teen.progress.levelTitleMaster';
  if (level >= 10) return 'teen.progress.levelTitlePro';
  if (level >= 5) return 'teen.progress.levelTitleRising';
  return 'teen.progress.levelTitleStarter';
};
